import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type {
  AssignmentStatus,
  AvailabilityResponse,
  EventStatus,
  Service,
} from '../types/db'
import { humanize } from '../lib/format'

/* -------------------------------------------------------------------------- */
/* Layout primitives                                                          */
/* -------------------------------------------------------------------------- */

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: ReactNode
  action?: ReactNode
}) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {subtitle ? <p className="page-header__subtitle">{subtitle}</p> : null}
      </div>
      {action ? <div className="page-header__action">{action}</div> : null}
    </header>
  )
}

export function Section({
  title,
  count,
  action,
  children,
}: {
  title: string
  count?: number
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="section">
      <div className="section__head">
        <h2>
          {title}
          {typeof count === 'number' ? <span className="section__count">{count}</span> : null}
        </h2>
        {action}
      </div>
      {children}
    </section>
  )
}

export function Card({
  children,
  to,
  tone,
  className,
}: {
  children: ReactNode
  to?: string
  tone?: 'default' | 'attention' | 'muted'
  className?: string
}) {
  const classes = ['card', tone && tone !== 'default' ? `card--${tone}` : '', className ?? '']
    .filter(Boolean)
    .join(' ')
  if (to) {
    return (
      <Link className={classes} to={to}>
        {children}
      </Link>
    )
  }
  return <div className={classes}>{children}</div>
}

export function StatTile({
  label,
  value,
  hint,
  tone,
  to,
}: {
  label: string
  value: ReactNode
  hint?: string
  tone?: 'default' | 'attention'
  to?: string
}) {
  const inner = (
    <>
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
      {hint ? <span className="stat__hint">{hint}</span> : null}
    </>
  )
  const classes = `stat${tone === 'attention' ? ' stat--attention' : ''}`
  return to ? (
    <Link className={classes} to={to}>
      {inner}
    </Link>
  ) : (
    <div className={classes}>{inner}</div>
  )
}

/* -------------------------------------------------------------------------- */
/* States                                                                     */
/* -------------------------------------------------------------------------- */

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p>{label}</p>
    </div>
  )
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state state--error" role="alert">
      <p className="state__title">Something went wrong</p>
      <p className="state__body">{message}</p>
      {onRetry ? (
        <button type="button" className="btn btn--ghost" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  )
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string
  body?: string
  action?: ReactNode
}) {
  return (
    <div className="state state--empty">
      <p className="state__title">{title}</p>
      {body ? <p className="state__body">{body}</p> : null}
      {action}
    </div>
  )
}

export function Notice({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'warning' | 'success'
  title?: string
  children: ReactNode
}) {
  return (
    <div className={`notice notice--${tone}`}>
      {title ? <p className="notice__title">{title}</p> : null}
      <div className="notice__body">{children}</div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Badges                                                                     */
/* -------------------------------------------------------------------------- */

type BadgeTone = 'neutral' | 'positive' | 'warning' | 'danger' | 'info'

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: BadgeTone
}) {
  return <span className={`badge badge--${tone}`}>{children}</span>
}

const EVENT_TONE: Record<EventStatus, BadgeTone> = {
  draft: 'neutral',
  scheduled: 'info',
  staffed: 'positive',
  completed: 'neutral',
  cancelled: 'danger',
}

export function EventStatusBadge({ status }: { status: EventStatus }) {
  return <Badge tone={EVENT_TONE[status]}>{humanize(status)}</Badge>
}

const AVAILABILITY_TONE: Record<AvailabilityResponse, BadgeTone> = {
  pending: 'warning',
  available: 'positive',
  unavailable: 'danger',
  tentative: 'info',
}

const AVAILABILITY_LABEL: Record<AvailabilityResponse, string> = {
  pending: 'Not answered',
  available: 'Available',
  unavailable: 'Not available',
  tentative: 'Maybe',
}

export function AvailabilityBadge({ response }: { response: AvailabilityResponse }) {
  return <Badge tone={AVAILABILITY_TONE[response]}>{AVAILABILITY_LABEL[response]}</Badge>
}

const ASSIGNMENT_TONE: Record<AssignmentStatus, BadgeTone> = {
  assigned: 'warning',
  confirmed: 'positive',
  cancelled: 'danger',
  completed: 'neutral',
  no_show: 'danger',
}

const ASSIGNMENT_LABEL: Record<AssignmentStatus, string> = {
  assigned: 'Confirm needed',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  completed: 'Worked',
  no_show: 'No show',
}

export function AssignmentBadge({ status }: { status: AssignmentStatus }) {
  return <Badge tone={ASSIGNMENT_TONE[status]}>{ASSIGNMENT_LABEL[status]}</Badge>
}

/* -------------------------------------------------------------------------- */
/* Service marker                                                             */
/* -------------------------------------------------------------------------- */

export function ServiceTag({ service }: { service: Pick<Service, 'name' | 'color_hex'> }) {
  return (
    <span className="service-tag">
      {/* color_hex is nullable in the schema; fall back to the CSS token. */}
      <span
        className="service-tag__dot"
        style={{ background: service.color_hex ?? 'var(--accent)' }}
      />
      {service.name}
    </span>
  )
}

export function Avatar({ initials, size = 40 }: { initials: string; size?: number }) {
  return (
    <span
      className="avatar"
      style={{ width: size, height: size, fontSize: Math.round(size / 2.6) }}
      aria-hidden="true"
    >
      {initials}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Progress                                                                   */
/* -------------------------------------------------------------------------- */

export function ProgressBar({
  value,
  max,
  label,
}: {
  value: number
  max: number
  label?: string
}) {
  const pct = max === 0 ? 0 : Math.round((value / max) * 100)
  return (
    <div className="progress" role="img" aria-label={label ?? `${value} of ${max}`}>
      <div className="progress__fill" style={{ width: `${pct}%` }} />
    </div>
  )
}

export function DemoRibbon() {
  return (
    <div className="demo-ribbon">
      Demo data. Nothing here is real, and nothing you tap is saved to a server.
    </div>
  )
}
